import React, { useState, useEffect } from 'react';
import { getEntries, addEntry, deleteEntry } from '../utils/api';
import { encrypt, decrypt } from '../utils/crypto';
import { generate } from 'generate-password';

function Dashboard({ encSalt, masterPassword, setIsAuthenticated }) {
  const [entries, setEntries] = useState([]);
  const [service, setService] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');

  // Fetch entries on component mount
  useEffect(() => {
    fetchEntries();
  }, []);

  // Function to fetch and decrypt entries
  const fetchEntries = async () => {
    try {
      const data = await getEntries();
      const decryptedEntries = await Promise.all(
        data.map(async (entry) => ({
          ...entry,
          password: await decrypt(entry.encrypted_password, masterPassword, encSalt),
          notes: entry.encrypted_notes ? await decrypt(entry.encrypted_notes, masterPassword, encSalt) : '',
        }))
      );
      setEntries(decryptedEntries);
    } catch (err) {
      console.error('Failed to fetch entries:', err);
    }
  };

  // Handle adding a new entry
  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const encryptedPassword = await encrypt(password, masterPassword, encSalt);
      const encryptedNotes = notes ? await encrypt(notes, masterPassword, encSalt) : null;
      await addEntry({
        service_name: service,
        username,
        encrypted_password: encryptedPassword,
        encrypted_notes: encryptedNotes,
      });
      fetchEntries(); // Refresh the list
      setService('');
      setUsername('');
      setPassword('');
      setNotes('');
    } catch (err) {
      console.error('Failed to add entry:', err);
    }
  };

  // Handle deleting an entry
  const handleDelete = async (id) => {
    try {
      await deleteEntry(id);
      fetchEntries(); // Refresh the list
    } catch (err) {
      console.error('Failed to delete entry:', err);
    }
  };

  // Generate a random password and copy to clipboard
  const generatePassword = () => {
    const newPassword = generate({
      length: 16,
      numbers: true,
      symbols: true,
      uppercase: true,
      lowercase: true,
    });
    setPassword(newPassword);
    navigator.clipboard.writeText(newPassword);
  };

  // Filter entries based on search input
  const filteredEntries = entries.filter((entry) =>
    entry.service_name.toLowerCase().includes(search.toLowerCase())
  );

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('sessionToken');
    setIsAuthenticated(false);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between mb-6">
        <h1 className="text-3xl">Password Manager</h1>
        <button
          onClick={handleLogout}
          className="p-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Logout
        </button>
      </div>

      {/* Form to add a new entry */}
      <form onSubmit={handleAdd} className="mb-6 p-4 bg-white dark:bg-gray-800 rounded shadow">
        <input
          value={service}
          onChange={(e) => setService(e.target.value)}
          placeholder="Service Name"
          className="w-full p-2 mb-2 border rounded dark:bg-gray-700 dark:text-white"
        />
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Username"
          className="w-full p-2 mb-2 border rounded dark:bg-gray-700 dark:text-white"
        />
        <div className="flex mb-2">
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"
          />
          <button
            type="button"
            onClick={generatePassword}
            className="ml-2 p-2 bg-green-500 text-white rounded hover:bg-green-600"
          >
            Generate & Copy
          </button>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)"
          className="w-full p-2 mb-2 border rounded dark:bg-gray-700 dark:text-white"
        />
        <button type="submit" className="w-full p-2 bg-blue-500 text-white rounded hover:bg-blue-600">
          Add Entry
        </button>
      </form>

      {/* Search bar */}
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search entries..."
        className="w-full p-2 mb-4 border rounded dark:bg-gray-700 dark:text-white"
      />

      {/* List of entries */}
      <ul className="space-y-4">
        {filteredEntries.map((entry) => (
          <li key={entry.id} className="p-4 bg-white dark:bg-gray-800 rounded shadow flex justify-between">
            <div>
              <p><strong>{entry.service_name}</strong></p>
              <p>Username: {entry.username}</p>
              <p>Password: {entry.password}</p>
              {entry.notes && <p>Notes: {entry.notes}</p>}
            </div>
            <button
              onClick={() => handleDelete(entry.id)}
              className="p-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Delete
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default Dashboard;